import {
	Hand,
	HelpCircle,
	Sparkles,
	GitBranch,
	UserPlus,
	Star,
	PhoneForwarded,
	Flag,
	X,
	Trash2,
} from "lucide-react";
import type { FlowNode, NodeType } from "@/lib/flow-types";
import { NODE_COLORS, NODE_LABELS } from "@/lib/flow-types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const ICONS: Record<NodeType, React.ElementType> = {
	greeting: Hand,
	question: HelpCircle,
	"ai-response": Sparkles,
	condition: GitBranch,
	"lead-capture": UserPlus,
	qualify: Star,
	handoff: PhoneForwarded,
	end: Flag,
};

interface NodeConfigPanelProps {
	node: FlowNode;
	onUpdate: (id: string, updates: Partial<FlowNode>) => void;
	onRemove: (id: string) => void;
	onClose: () => void;
}

export function NodeConfigPanel({
	node,
	onUpdate,
	onRemove,
	onClose,
}: NodeConfigPanelProps) {
	const Icon = ICONS[node.type];
	const color = NODE_COLORS[node.type];

	const updateConfig = (key: string, value: string | number) => {
		onUpdate(node.id, {
			config: { ...node.config, [key]: value },
		});
	};

	return (
		<div className="h-full flex flex-col">
			{/* Header */}
			<div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
				<div className="flex items-center gap-2.5">
					<div
						className="flex items-center justify-center w-8 h-8 rounded-lg"
						style={{ background: `${color}20` }}
					>
						<Icon
							size={16}
							style={{ color }}
							strokeWidth={2.5}
						/>
					</div>
					<div>
						<p className="text-sm font-bold text-zinc-200">
							{NODE_LABELS[node.type]}
						</p>
						<p className="text-[10px] text-zinc-500">
							{node.id}
						</p>
					</div>
				</div>
				<button
					onClick={onClose}
					className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.06] transition-colors"
				>
					<X size={14} />
				</button>
			</div>

			{/* Config fields */}
			<div className="flex-1 overflow-y-auto p-4 space-y-4">
				{/* Node label */}
				<div className="space-y-1.5">
					<Label className="text-xs font-semibold text-zinc-400">
						Step Name
					</Label>
					<Input
						value={node.label}
						onChange={(e) =>
							onUpdate(node.id, { label: e.target.value })
						}
						placeholder="Enter step name..."
						className="text-sm h-9"
					/>
				</div>

				<Separator />

				{/* Type-specific config */}
				{(node.type === "greeting" ||
					node.type === "question" ||
					node.type === "lead-capture" ||
					node.type === "handoff" ||
					node.type === "end") && (
					<div className="space-y-1.5">
						<Label className="text-xs font-semibold text-zinc-400">
							Message
						</Label>
						<Textarea
							value={node.config.message || ""}
							onChange={(e) =>
								updateConfig("message", e.target.value)
							}
							placeholder="What should the bot say?"
							rows={3}
							className="text-sm resize-none"
						/>
					</div>
				)}

				{node.type === "ai-response" && (
					<div className="space-y-3">
						<div className="space-y-1.5">
							<Label className="text-xs font-semibold text-zinc-400">
								AI Prompt
							</Label>
							<Textarea
								value={node.config.prompt || ""}
								onChange={(e) =>
									updateConfig("prompt", e.target.value)
								}
								placeholder="Instructions for the AI..."
								rows={4}
								className="text-sm resize-none"
							/>
						</div>
						<div className="rounded-lg bg-purple-500/10 border border-purple-500/15 p-2.5">
							<p className="text-[10px] text-purple-400 font-medium">
								The AI will use past chat context to generate personalized responses based on this prompt.
							</p>
						</div>
					</div>
				)}

				{node.type === "condition" && (
					<div className="space-y-3">
						<div className="space-y-1.5">
							<Label className="text-xs font-semibold text-zinc-400">
								Condition
							</Label>
							<Input
								value={node.config.condition || ""}
								onChange={(e) =>
									updateConfig("condition", e.target.value)
								}
								placeholder="e.g., lead_score >= 70"
								className="text-sm h-9 font-mono"
							/>
						</div>
						<div className="grid grid-cols-2 gap-2">
							<div className="space-y-1.5">
								<Label className="text-[10px] font-semibold text-emerald-400">
									True Path Label
								</Label>
								<Input
									value={node.config.trueLabel || ""}
									onChange={(e) =>
										updateConfig(
											"trueLabel",
											e.target.value
										)
									}
									placeholder="Yes"
									className="text-xs h-8"
								/>
							</div>
							<div className="space-y-1.5">
								<Label className="text-[10px] font-semibold text-red-400">
									False Path Label
								</Label>
								<Input
									value={node.config.falseLabel || ""}
									onChange={(e) =>
										updateConfig(
											"falseLabel",
											e.target.value
										)
									}
									placeholder="No"
									className="text-xs h-8"
								/>
							</div>
						</div>
					</div>
				)}

				{node.type === "lead-capture" && (
					<div className="space-y-1.5">
						<Label className="text-xs font-semibold text-zinc-400">
							Capture Field
						</Label>
						<Input
							value={node.config.field || ""}
							onChange={(e) =>
								updateConfig("field", e.target.value)
							}
							placeholder="e.g., email, phone, company"
							className="text-sm h-9"
						/>
					</div>
				)}

				{node.type === "qualify" && (
					<div className="space-y-3">
						<div className="space-y-1.5">
							<Label className="text-xs font-semibold text-zinc-400">
								Qualification Threshold
							</Label>
							<div className="flex items-center gap-3">
								<input
									type="range"
									min={0}
									max={100}
									value={node.config.qualifyScore || 50}
									onChange={(e) =>
										updateConfig(
											"qualifyScore",
											parseInt(e.target.value)
										)
									}
									className="flex-1 accent-cyan-500"
								/>
								<span
									className="text-sm font-bold w-10 text-center"
									style={{ color }}
								>
									{node.config.qualifyScore || 50}
								</span>
							</div>
						</div>
						<div className="space-y-1.5">
							<Label className="text-xs font-semibold text-zinc-400">
								Qualifying Criteria
							</Label>
							<Input
								value={node.config.condition || ""}
								onChange={(e) =>
									updateConfig("condition", e.target.value)
								}
								placeholder="e.g., interested in demo OR pricing"
								className="text-sm h-9"
							/>
						</div>
					</div>
				)}

				{node.type === "handoff" && (
					<div className="space-y-1.5">
						<Label className="text-xs font-semibold text-zinc-400">
							Handoff Email
						</Label>
						<Input
							value={node.config.handoffEmail || ""}
							onChange={(e) =>
								updateConfig("handoffEmail", e.target.value)
							}
							placeholder="sales@company.com"
							className="text-sm h-9"
						/>
					</div>
				)}
			</div>

			{/* Footer */}
			<div className="border-t border-white/[0.06] p-3">
				<Button
					variant="destructive"
					size="sm"
					className="w-full text-xs"
					onClick={() => onRemove(node.id)}
				>
					<Trash2 size={12} className="mr-1.5" />
					Delete Step
				</Button>
			</div>
		</div>
	);
}
